import { PHONE_DIALPAD_KEYS } from '@/constants/phoneDialpad';
import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  InputWrapper,
  Loader,
  Popover,
  rem,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import { IconPhone, IconPhoneCall } from '@tabler/icons-react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { trackCallInitiated } from '../services/analytics';
import { CallerIdSelect } from './CallerIdSelect';

export function GlobalPhoneControls(): ReactElement {
  const { connectionStatus, makeCall, acceptCall, rejectCall, availableCallerIds } = useTwilioPhone();
  const posthog = usePostHog();

  const [dialerOpened, { toggle: toggleDialer, open: openDialer }] = useDisclosure(false);
  const [phoneNumber, setPhoneNumber] = useState('+1 ');
  const [isCallingInProgress, setIsCallingInProgress] = useState(false);

  // Transform availableCallerIds to CompanyPhoneNumber format for the component
  const companyPhoneNumbers = useMemo(() => {
    return availableCallerIds.map((phone, index) => ({
      id: phone.callerId, // Use the actual phone number as ID
      number: phone.callerId,
      label: phone.friendlyName || `Line ${index + 1}`,
    }));
  }, [availableCallerIds]);

  const [selectedCallerId, setSelectedCallerId] = useLocalStorage<string | null>({
    key: 'selected-caller-id',
    defaultValue: null,
  });

  // Auto-select first caller ID if none selected and options are available
  useEffect(() => {
    if (!selectedCallerId && companyPhoneNumbers.length > 0) {
      setSelectedCallerId(companyPhoneNumbers[0].id);
    }
  }, [companyPhoneNumbers, selectedCallerId, setSelectedCallerId]);

  const handleEnterKey = () => {
    if (phoneNumber.length >= 11) {
      handleMakeCall();
    }
  };

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; action?: string; payload?: unknown } | undefined;
      if (!data || data.type !== 'CALL_ACTION') return;
      const action = data.action;
      // The push names its call, so a push that outlived it cannot act on another (BAR-2531).
      const notificationCallSid = (data.payload as { call?: { callSid?: string } } | undefined)?.call?.callSid;
      if (action === 'accept') {
        void acceptCall({ notificationCallSid });
      } else if (action === 'reject') {
        void rejectCall({ notificationCallSid });
      } else if (action === 'call-back') {
        const payload = (data.payload ?? {}) as { call?: { from?: string } };
        const from = payload.call?.from;
        if (from) {
          void makeCall(normalizePhoneNumber(from));
        } else {
          openDialer();
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handler);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handler);
      }
    };
  }, [acceptCall, rejectCall, makeCall, openDialer]);

  const handleMakeCall = async () => {
    if (phoneNumber.trim() && !isCallingInProgress) {
      setIsCallingInProgress(true);
      try {
        const normalizedNumber = normalizePhoneNumber(phoneNumber);

        // The selected caller ID is already the phone number (we use callerId as the ID)
        const callerIdNumber = selectedCallerId;

        // Track call initiated from global phone controls
        // Note: We don't have patient_id here since it's a direct dial
        trackCallInitiated(posthog, {
          source: 'global phone controls',
          patient_id: 'unknown', // Direct dial doesn't have patient context
        });

        await makeCall(normalizedNumber, callerIdNumber || undefined);
        setPhoneNumber('');
        toggleDialer();
      } finally {
        setIsCallingInProgress(false);
      }
    }
  };

  const handleDigitClick = (digit: string) => {
    setPhoneNumber((prev) => {
      const newNumber = prev + digit;
      return newNumber.replace(/[^0-9]/g, '');
    });
  };
  return (
    <>
      <Popover opened={dialerOpened} onChange={toggleDialer} width={260} position="bottom-end" withArrow shadow="md">
        <Popover.Target>
          <ActionIcon variant="subtle" size="md" style={{ position: 'relative' }} onClick={toggleDialer}>
            {connectionStatus === 'offline' && <Loader size={10} style={{ position: 'absolute', top: 0, right: 0 }} />}
            <IconPhone size={20} />
          </ActionIcon>
        </Popover.Target>

        <Popover.Dropdown>
          <Stack gap="xs">
            {connectionStatus === 'online' && (
              <>
                {/* Caller ID Selection */}
                {companyPhoneNumbers.length > 0 && (
                  <Box
                    onClick={(e) => {
                      // Prevent clicks within the CallerIdSelect from closing the Popover
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      // Prevent mousedown events from closing the Popover
                      e.stopPropagation();
                    }}
                  >
                    <CallerIdSelect
                      phoneNumbers={companyPhoneNumbers}
                      value={selectedCallerId}
                      onChange={setSelectedCallerId}
                    />
                  </Box>
                )}

                <InputWrapper label="Number to Call" required withAsterisk>
                  <PhoneInput
                    country={'us'}
                    enableSearch
                    disableCountryGuess
                    countryCodeEditable={false}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    inputStyle={{
                      width: '100%',
                      border: '1px solid var(--mantine-color-gray-4)',
                    }}
                    buttonStyle={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--mantine-color-gray-4)',
                    }}
                    inputProps={{
                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          handleEnterKey();
                        }
                      },
                    }}
                    containerStyle={{ width: '100%' }}
                  />
                </InputWrapper>

                <Box
                  display="grid"
                  style={{
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: rem(6),
                    textAlign: 'center',
                  }}
                >
                  {PHONE_DIALPAD_KEYS.map((item) => (
                    <Button
                      key={item.label}
                      onClick={() => handleDigitClick(item.label)}
                      variant="subtle"
                      size="sm"
                      radius="md"
                      h={56}
                    >
                      <Stack gap={0} align="center" justify="center" w="100%">
                        <Text fz="lg" c="green.9">
                          {item.label}
                        </Text>
                        <Text fz="xs" c="dimmed" lh={1}>
                          {item.sub}
                        </Text>
                      </Stack>
                    </Button>
                  ))}
                </Box>
                <Group justify="flex-end" mt="xs">
                  <Tooltip label="Enter a valid phone number" disabled={phoneNumber.length >= 14}>
                    <Button
                      variant="primary"
                      leftSection={<IconPhoneCall size={16} />}
                      radius="xl"
                      onClick={handleMakeCall}
                      disabled={phoneNumber.length < 1}
                    >
                      Call
                    </Button>
                  </Tooltip>
                </Group>
              </>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </>
  );
}
